import { browserStore, installAudioControls, mutedAtStart } from './controls.js';
import { AudioEngine, type Visibility } from './engine.js';

type Store = { getItem(key: string): string | null; setItem(key: string, value: string): void };

/**
 * The page's sound as main.ts starts it (ADR-034). A played lesson (`?play=`)
 * and the demo remember the viewer's mute for the page; a class headset
 * (`boot`) never does -- a preview's "Geluid: uit" on a shared Quest must not
 * silence tomorrow's class, where nothing but `?sound=0` mutes and the
 * teacher's switch decides.
 */
export function pageAudio(
  kind: 'class' | 'play' | 'demo',
  deps: {
    href: string;
    storage?: () => Store | undefined;
    create: () => AudioContext;
    install?: typeof installAudioControls;
  },
): AudioEngine {
  const storage = kind === 'class' ? undefined : (deps.storage ?? browserStore)();
  const audio = new AudioEngine(deps.create, { muted: mutedAtStart(deps.href, storage) });
  (deps.install ?? installAudioControls)(audio, storage ? { storage } : {});
  return audio;
}

/**
 * An XR session's visibility, as the engine's `xr` source for as long as it
 * runs: behind the system menu it fades, taken off it suspends, and the page's
 * own visibility counts again once the session has ended.
 */
export function followXrVisibility(
  session: XRSession,
  audio: { setVisibility(visibility: Visibility | undefined, source: 'page' | 'xr'): void },
): void {
  audio.setVisibility(session.visibilityState, 'xr');
  session.addEventListener('visibilitychange', () =>
    audio.setVisibility(session.visibilityState, 'xr'),
  );
  session.addEventListener('end', () => audio.setVisibility(undefined, 'xr'));
}
